/*  eslint-disable @typescript-eslint/no-unsafe-member-access, no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call */
import { ACCESS_TOKEN_NAME } from 'constants/access-token';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { AppProps } from 'next/app';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache, { EmotionCache } from '@emotion/cache';

import Head from 'next/head';

import { RecoilRoot } from 'recoil';
import { SWRConfig, SWRConfiguration } from 'swr';
import { SnackbarProvider, SnackbarProviderProps } from 'notistack';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import * as Sentry from '@sentry/nextjs';

import { ApiResult } from 'services/api/result.type';

import '../app.css';
import { useIsFullWidth, useProfile, usePushNotifications } from 'hooks';
import { useIntercomApi, useProfileApi } from 'services/api';
import { API_URL, RECAPTCHA_SITE_KEY } from 'config/app.config';
import { GTM_ID } from 'config/gtm.config';
import InstallPromptModal from 'components/modals/InstallPromptModal';
import features from 'config/features';
import { useRouter } from 'next/router';
import { theme } from '../theme';
import NotificationPermissionPrompt from '../components/modals/NotificationPermissionPrompt';

const clientSideEmotionCache = createCache({ key: 'css', prepend: true });

export const fetcher = async <T extends never, P extends Parameters<T>>(
  key: string | ((...args: P) => ApiResult<T>),
  ...args: P
): Promise<T | undefined> => {
  try {
    const headers = new Headers();

    const token = window?.localStorage.getItem(ACCESS_TOKEN_NAME);
    if (token) headers.append('Authorization', `Bearer: ${token}`);

    if (typeof key === 'string') {
      const url = API_URL + key;
      const response = await fetch(url, {
        credentials: 'include',
        headers,
      });

      if (response.status !== 200) throw new Error(`Failed to load: ${key}`);

      return response.json() as T;
    }

    const result = await key(...args);

    if (result.success) return result.value;

    if (result.error.response?.status === 401) return;

    console.error('Fetcher error:', result.error);
    Sentry.captureException(result.error);
    return undefined;
  } catch (error) {
    console.error('Fetcher unexpected error:', error);
    Sentry.captureException(error);
    return undefined;
  }
};

const swrConfig: SWRConfiguration = {
  fetcher,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

const snackbarProviderProps: Omit<SnackbarProviderProps, 'children'> = {
  dense: true,
  maxSnack: 2,
  anchorOrigin: {
    horizontal: 'center',
    vertical: 'bottom',
  },
  autoHideDuration: 3236,
};

interface CampusLushProps extends AppProps {
  emotionCache?: EmotionCache;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function MyApp ({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: CampusLushProps): ReactElement {
  const router = useRouter();
  const ProfileApi = useProfileApi();

  /* Google Analytics */
  useEffect(() => {
    if (!GTM_ID) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_ID;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer ?? [
      { 'gtm.start': +new Date(), event: 'gtm.js' },
    ];

    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(...args);
    };
  }, []);

  const { profile } = useProfile();
  const isFullWidth = useIsFullWidth();
  const intercomApi = useIntercomApi();
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const { permission, requestPermission, isSupported } = usePushNotifications();

  // Register Service Worker (includes Firebase messaging for push notifications)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .catch(error => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/download') return;

    if (!features.showInstallPrompt) return;

    if ('onbeforeinstallprompt' in window) {
      const handleInstallPrompt = (e: any) => {
        e.preventDefault();
        setTimeout(() => {
          setInstallPrompt(e);
        }, 5 * 1000);
      };
      window.addEventListener('beforeinstallprompt', handleInstallPrompt);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      };
    }
  }, []);

  /* Intercom */
  // Show notification permission prompt for logged-in users on web (not mobile app)
  useEffect(() => {
    const isMobileApp = typeof window !== 'undefined' && window.ReactNativeWebView;
    const hasAskedBefore = localStorage.getItem('notificationPromptDismissed');

    if (
      profile &&
      profile.id &&
      isSupported &&
      permission === 'default' &&
      !isMobileApp &&
      !hasAskedBefore
    ) {
      // Delay showing the prompt to not overwhelm the user
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 10000); // Show after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [profile, permission, isSupported]);

  useEffect(() => {
    if (profile && profile.id) handleDeviceTokenMobileApp();

    if (!profile || !isFullWidth) {
      if (typeof window.Intercom === 'function') window.Intercom('shutdown');

      return;
    }

    void (async () => {
      const response = await intercomApi.getIntercomSettings();
      if (!response.success || !response.value.appId) return;

      const { appId } = response.value;

      const intercomSettings = {
        api_base: 'https://api-iam.intercom.io',
        app_id: appId,
        user_id: profile.id,
        user_hash: response.value.userHash,
        name: [profile.firstName, profile.lastName].join(' '),
        email: profile.primaryEmail,
        school: profile.school?.name,
        school_domain: profile.school?.domainName,
      } as const;

      window.intercomSettings = intercomSettings;
      const ic = window.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', intercomSettings);
      } else {
        const i = function (...args: unknown[]) {
          i.c(args);
        };
        i.booted = false;
        i.q = [] as unknown[];
        i.c = function (args: unknown[]) {
          i.q.push(args);
        };
        window.Intercom = i as Intercom_.IntercomStatic;
        const loadWidget = function () {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.src = `https://widget.intercom.io/widget/${appId}`;
          //script.innerHTML= `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`
          document.head.appendChild(script);
        };
        if (document.readyState === 'complete') loadWidget();
        else window.addEventListener('load', loadWidget, false);
      }
    })();
  }, [profile, isFullWidth]);

  const styles = useMemo(
    () => ({
      '.keyboardFocus': {
        outline: 'none',
        '&:focus-visible': {
          outline: `2px auto ${theme.palette.primary.main}`,
          outlineOffset: 6,
        },
      },
      '@media print': {
        body: {
          color: '#000',
        },
        'body::backdrop': {
          background: '#fff',
        },
      },
    }),
    [],
  );

  const handleInstall = () => {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => {
        setInstallPrompt(null);
      });
    }
  };

  const handleClose = () => {
    setInstallPrompt(null);
  };

  const handleNotificationPermissionRequest = async () => {
    setShowNotificationPrompt(false);
    await requestPermission();
  };

  const handleNotificationPromptClose = () => {
    localStorage.setItem('notificationPromptDismissed', 'true');
    setShowNotificationPrompt(false);
  };

  const handleDeviceTokenMobileApp = () => {
    window?.ReactNativeWebView?.postMessage(
      JSON.stringify({
        action: 'getDeviceToken',
      }),
    );
  };

  const mobileAppBridgeHandler = (message: any) => {
    if (message.data && typeof message.data === 'string') {
      if (message.data === 'recaptcha-setup')
        return;
      const data = JSON.parse(message.data as string);
      /**
       * DEVICE TOKEN ACTION
       */
      if (data.action === 'deviceToken') {
        const deviceToken = data.token;
        if (deviceToken) {
          void (async () => {
            await ProfileApi.updateDeviceToken({ deviceToken });
          })();
        }
      } else if (data.action === 'redirect-url') {
        const { redirectUrl } = data.data;
        if (redirectUrl) router.push(redirectUrl as string);
      }
    }
  };

  useEffect(() => {
    function handleEvent (message: any) {
      mobileAppBridgeHandler(message);
    }
    if (navigator.appVersion.includes('Android'))
      document.addEventListener('message', handleEvent);
    else window.addEventListener('message', handleEvent);
  }, []);

  return (
    <>
      <Sentry.ErrorBoundary fallback={<h1>Unexpected error</h1>}>
        <CacheProvider value={emotionCache}>
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, minimum-scale=1"
            />
          </Head>
          <ThemeProvider theme={theme}>
            <SnackbarProvider {...snackbarProviderProps}>
              <SWRConfig value={swrConfig}>
                <GoogleReCaptchaProvider
                  reCaptchaKey={RECAPTCHA_SITE_KEY}
                  scriptProps={{
                    async: false,
                    defer: false,
                    appendTo: 'head',
                    nonce: undefined,
                  }}
                >
                  <RecoilRoot>
                    <CssBaseline />
                    <GlobalStyles styles={styles} />
                    <Component {...pageProps} />
                    <InstallPromptModal
                      open={Boolean(installPrompt)}
                      onClose={handleClose}
                      onInstall={handleInstall}
                    />
                    <NotificationPermissionPrompt
                      open={showNotificationPrompt}
                      onClose={handleNotificationPromptClose}
                      onEnable={handleNotificationPermissionRequest}
                    />
                  </RecoilRoot>
                </GoogleReCaptchaProvider>
              </SWRConfig>
            </SnackbarProvider>
          </ThemeProvider>
        </CacheProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

export default MyApp;
